<template>
  <div class="m-sim" :class="{ 'has-bottom': !!state }">
    <!-- ═══ 账户栏（吸顶，滚动时始终可切账户） ═══ -->
    <div class="sim-top">
      <van-dropdown-menu v-if="accounts.length" class="acct-menu" active-color="#1989fa">
        <van-dropdown-item
          v-model="accountId"
          :options="accountOptions"
          @change="onAccountChange"
        />
      </van-dropdown-menu>
      <span v-else class="acct-none">暂无账户</span>
      <van-icon name="replay" class="top-icon" @click="loadDashboard" />
      <van-icon name="setting-o" class="top-icon" @click="openAccountActions" />
    </div>

    <van-loading v-if="loading && !state" class="page-loading" vertical>加载中...</van-loading>

    <van-empty v-else-if="!loading && !accounts.length" description="还没有模拟账户">
      <van-button round type="info" size="small" @click="openCreate">新建账户</van-button>
    </van-empty>

    <van-empty v-else-if="!state && error" :description="error" />

    <template v-else-if="state">
      <!-- ═══ 资产概览 ═══ -->
      <div class="asset-card">
        <div class="asset-head">
          <span class="asset-label">总资产</span>
          <van-tag plain size="mini" :type="account.isManual ? 'default' : 'primary'">
            {{ account.isManual ? '手动盘' : account.strategyTag }}
          </van-tag>
        </div>
        <div class="asset-value">{{ fmtMoney(summary.equity) }}</div>
        <div class="asset-pnl">
          <span :class="chgClass(summary.totalPnl)">{{ fmtSigned(summary.totalPnl) }}</span>
          <span :class="chgClass(summary.totalPnlPct)">{{ fmtPct(summary.totalPnlPct) }}</span>
          <span class="muted">初始 {{ fmtMoney(summary.initialCash) }}</span>
        </div>

        <div class="asset-grid">
          <div class="g-cell">
            <span class="g-label">今日盈亏</span>
            <b :class="chgClass(summary.dayPnl)">{{ fmtSigned(summary.dayPnl) }}</b>
          </div>
          <div class="g-cell">
            <span class="g-label">仓位</span>
            <b>{{ positionPct }}%</b>
          </div>
          <div class="g-cell">
            <span class="g-label">可用现金</span>
            <b>{{ fmtMoney(summary.cash) }}</b>
          </div>
          <div class="g-cell">
            <span class="g-label">持仓</span>
            <b>{{ summary.positionCount }} 只</b>
          </div>
          <div class="g-cell">
            <span class="g-label">已实现</span>
            <b :class="chgClass(summary.realizedPnl)">{{ fmtSigned(summary.realizedPnl) }}</b>
          </div>
          <div class="g-cell">
            <span class="g-label">组合回撤</span>
            <b :class="isDrawdownWarn ? 'down-strong' : ''">{{ fmtPct(summary.drawdown) }}</b>
          </div>
        </div>

        <div class="asset-foot">
          <span>费用累计 {{ fmtMoney(summary.feeTotal) }}</span>
          <span>峰值 {{ fmtMoney(summary.peakEquity) }}</span>
          <span>行情 {{ quoteFreshness }}</span>
        </div>

        <div class="asset-signal">
          <template v-if="account.isManual">
            <span class="muted">手动盘不跟策略 · 买卖请在「手动交易」里自行下单</span>
          </template>
          <template v-else-if="signal">
            <span class="muted">
              信号 {{ signal.signalDate }} · 持有 {{ signal.hold }} 交易日 · 到期 {{ signal.exitDate || '—' }}
              · 共 {{ signal.picks.length }} 只等权
            </span>
          </template>
          <template v-else>
            <span class="warn-text">策略 {{ account.strategyTag }} 无当期信号，无法生成调仓建议</span>
          </template>
          <span class="muted">风控 止损 -{{ -thresholds.stopLoss }}% / 止盈 {{ thresholds.takeProfit }}% / 单只 ≤ {{ thresholds.maxPosition }}%</span>
        </div>
      </div>

      <van-notice-bar
        wrapable
        :scrollable="false"
        :background="tradeWindow.marketOpen ? '#ecf9ff' : '#f4f4f5'"
        :color="tradeWindow.marketOpen ? '#1989fa' : '#969799'"
        :text="sessionText"
      />

      <!-- ═══ 待确认操作（行动项，放在最显眼处） ═══ -->
      <div class="sug-card" :class="{ 'is-empty': !pendingSuggestions.length }">
        <div class="sug-head">
          <span class="dot" />
          <span class="sug-title">待确认操作 {{ pendingSuggestions.length }}</span>
          <!-- 已忽略的要说出来：否则用户看到「建议变少了」会以为策略自己改了主意 -->
          <span v-if="ignoredCount" class="sug-note is-ignored">已忽略 {{ ignoredCount }} 条</span>
          <span class="spacer" />
          <van-button
            v-if="pendingSuggestions.length > 1"
            size="mini"
            type="info"
            plain
            :loading="bulkRunning"
            @click="tradeAll"
          >全部成交</van-button>
        </div>

        <div v-if="pendingSuggestions.length" class="sug-body">
          <div
            v-for="(s, i) in pendingSuggestions"
            :key="s.side + s.code + i"
            class="sug-row"
          >
            <div class="sug-l1">
              <van-tag :type="s.side === 'buy' ? 'danger' : 'success'" size="medium">
                {{ SIDE_CN[s.side] }}
              </van-tag>
              <span class="sug-stock">
                <a :href="stockstarCodeLink(s.code)" target="_blank" rel="noopener noreferrer">{{ s.code }}</a>
                <a :href="eastmoneyCodeLink(s)" target="_blank" rel="noopener noreferrer">{{ s.name || '—' }}</a>
              </span>
              <span class="spacer" />
              <!-- 不足 1 手时 amount 为 0，显示「—」比 ¥0.00 更少误导 -->
              <span class="sug-amount">{{ s.amount > 0 ? fmtMoney(s.amount) : '—' }}</span>
            </div>
            <div class="sug-l2">
              <span v-if="s.belowLot" class="warn-text">
                目标 {{ fmtMoney(s.targetAmount) }} 不足 1 手（现价 {{ fmtPrice(s.price) }}）
              </span>
              <span v-else-if="s.price">
                {{ fmtPrice(s.price) }} × {{ (s.shares || 0).toLocaleString('zh-CN') }} 股
              </span>
              <span v-else class="warn-text">暂无行情，无法计算股数</span>
              <span class="sug-reason">{{ s.reason }}</span>
            </div>
            <div class="sug-l3">
              <span v-if="s.affordable === false" class="warn-text">现金不足</span>
              <span class="spacer" />
              <van-button
                size="mini"
                type="info"
                plain
                :disabled="!s.price || !s.shares || s.affordable === false"
                :loading="tradingKey === s.side + s.code"
                @click="tradeOne(s)"
              >模拟成交</van-button>
              <van-button
                size="mini"
                plain
                :loading="ignoringKey === s.side + s.code"
                title="本期信号内不再提示这条建议（会记一条历史，可在「预警」里撤销）"
                @click="ignore(s)"
              >忽略</van-button>
            </div>
          </div>
        </div>
        <div v-else class="sug-empty">
          <template v-if="ignoredCount">
            本期 {{ ignoredCount }} 条建议已忽略，下方「预警 → 已忽略的建议」可撤销
          </template>
          <template v-else>
            {{ account.isManual
              ? '手动盘无策略建议，点下方「手动交易」自行买入或卖出'
              : '当前没有需要调整的持仓' }}
          </template>
        </div>
      </div>

      <!-- ═══ 明细 ═══ -->
      <van-tabs v-model="activeTab" line-width="22" class="detail-tabs">
        <!-- 持仓 -->
        <van-tab :title="`持仓 ${positions.length}`">
          <div class="tab-bar">
            <van-checkbox v-model="onlyAbnormal" icon-size="14" shape="square">仅看异常</van-checkbox>
            <span class="spacer" />
            <span class="muted">市值 {{ fmtMoney(summary.marketValue) }}</span>
          </div>

          <van-empty v-if="!visiblePositions.length" image-size="72" :description="onlyAbnormal ? '没有异常持仓' : '暂无持仓'" />

          <div v-for="p in visiblePositions" :key="p.code" class="pos-card">
            <div class="pos-head">
              <a class="code" :href="stockstarCodeLink(p.code)" target="_blank" rel="noopener noreferrer">{{ p.code }}</a>
              <a class="pname" :href="eastmoneyCodeLink(p)" target="_blank" rel="noopener noreferrer">{{ p.name || '—' }}</a>
              <van-tag size="mini" :type="statusTagType(p)" plain>{{ statusText(p) }}</van-tag>
            </div>

            <div class="pos-line">
              <span>持股 <b>{{ p.shares.toLocaleString('zh-CN') }}</b></span>
              <span>成本 <b>{{ fmtPrice(p.costPrice) }}</b></span>
              <span>
                现价 <b>{{ fmtPrice(p.price) }}</b>
                <i v-if="p.priceSource !== 'realtime'" class="stale" :title="p.priceSource === 'daily' ? '非实时，按最近收盘估值' : '无行情，按成本价估值'">非实时</i>
              </span>
            </div>

            <div class="pos-line">
              <span>浮动 <b :class="chgClass(p.pnlPct)">{{ fmtPct(p.pnlPct) }}</b></span>
              <span>今日 <b :class="chgClass(p.pctChg)">{{ p.pctChg == null ? '—' : fmtPct(p.pctChg) }}</b></span>
              <span>权重 <b>{{ p.weight.toFixed(2) }}%</b></span>
            </div>

            <div class="pos-line">
              <span>市值 <b>{{ fmtMoney(p.marketValue) }}</b></span>
              <span>持有 <b>{{ p.heldDays }} 日</b></span>
              <span>盈亏 <b :class="chgClass(p.pnl)">{{ fmtSigned(p.pnl) }}</b></span>
            </div>

            <div class="pos-actions">
              <van-button size="mini" plain type="info" @click="openTrade(p)">交易</van-button>
              <van-button size="mini" plain @click="openCostEdit(p)">改成本</van-button>
            </div>
          </div>
        </van-tab>

        <!-- 流水 -->
        <van-tab :title="`流水 ${trades.length}`">
          <van-empty v-if="!trades.length" image-size="72" description="还没有成交记录" />
          <div v-for="t in trades" :key="t.id" class="flow-card">
            <div class="flow-l1">
              <van-tag :type="t.side === 'buy' ? 'danger' : 'success'" size="medium">{{ SIDE_CN[t.side] }}</van-tag>
              <span class="flow-stock">
                <a :href="stockstarCodeLink(t.code)" target="_blank" rel="noopener noreferrer">{{ t.code }}</a>
                <a :href="eastmoneyCodeLink(t)" target="_blank" rel="noopener noreferrer">{{ t.name || '—' }}</a>
              </span>
              <span class="spacer" />
              <span class="flow-date">{{ t.tradeDate }}</span>
            </div>
            <div class="flow-l2">
              <span>{{ fmtPrice(t.price) }} × {{ t.shares.toLocaleString('zh-CN') }} 股</span>
              <span>费用 {{ fmtMoney(t.fee) }}</span>
              <span v-if="t.realizedPnl != null">
                实现 <b :class="chgClass(t.realizedPnl)">{{ fmtSigned(t.realizedPnl) }}</b>
              </span>
            </div>
            <div v-if="t.reason" class="flow-reason">{{ t.reason }}</div>
          </div>
        </van-tab>

        <!-- 预警 -->
        <van-tab :title="alerts.length ? `预警 ${alerts.length}` : '预警'">
          <div v-if="alerts.length" class="alert-list">
            <div v-for="a in alerts" :key="a.type + a.code" class="alert-item">
              <span class="lv-dot" :style="{ background: levelColor(a.level) }" />
              <div class="alert-main">
                <div class="alert-msg" :style="{ color: levelColor(a.level) }">{{ a.message }}</div>
                <div v-if="a.detail" class="alert-detail">{{ a.detail }}</div>
              </div>
              <van-button
                size="mini"
                plain
                :loading="alertHandlingKey === alertKey(a)"
                @click="resolveAlert(a)"
              >已处理</van-button>
            </div>
          </div>
          <van-empty
            v-else
            image-size="72"
            :description="alertHandledCount
              ? `本期 ${alertHandledCount} 条预警已处理，下方「历史预警」里可撤销`
              : `暂无预警 · 止损 ${thresholds.stopLoss}% / 止盈 ${thresholds.takeProfit}% / 单只 ≤ ${thresholds.maxPosition}% / 回撤 ${thresholds.drawdown}%`"
          />

          <!--
            已忽略的调仓建议。
            为什么放在「预警」页签：这里是本页唯一的「历史/流水」区（下面就是历史预警），
            用户点完「忽略」要能马上看到「确实记下来了、还能撤销」。
            为什么单独一块、不和预警混排：忽略不是风险事件，是**用户决策**，
            混进预警会让「预警 N」这个数变得不可信。
          -->
          <div v-if="state && state.ignores && state.ignores.length" class="history ignores">
            <div class="history-head">
              已忽略的建议（本期生效 {{ activeIgnores.length }} 条 · 共 {{ state.ignores.length }} 条）
            </div>
            <div
              v-for="g in state.ignores"
              :key="'ig' + g.id"
              class="history-item"
              :class="{ 'is-handled': !g.active }"
            >
              <span class="history-date">{{ g.signalDate || '—' }}</span>
              <span class="history-msg is-plain">{{ SIDE_CN[g.side] }} {{ g.code }} {{ g.name }}</span>
              <span v-if="!g.active" class="history-flag">已失效</span>
              <van-button size="mini" plain class="undo-btn" @click="unignore(g)">撤销</van-button>
            </div>
          </div>

          <!--
            历史预警（风险事件台账）。
            ⚠️ 这里**必须**有操作入口：条件已不成立的旧预警不会再出现在上面的活动列表里
               （模拟盘换过仓），但它们在账上仍是 `handled = 0`
               ⇒ 首页「N 条模拟盘预警未处理」算的就是它们。
               没有入口 = 清不到 0 = 待办永远挂着（2026-10-01 用户报的第二个症状）。
          -->
          <div v-if="historyAlerts.length" class="history">
            <div class="history-head">
              <span class="history-head-text">
                历史预警（近 {{ historyAlerts.length }} 条<template v-if="alertPendingCount"> · 未处理 {{ alertPendingCount }} 条</template>）
              </span>
              <van-button
                v-if="alertPendingCount"
                size="mini"
                plain
                class="undo-btn head-action"
                :loading="alertBulkRunning"
                @click="resolveAllAlerts"
              >全部已处理</van-button>
            </div>
            <div
              v-for="h in historyAlerts"
              :key="h.id"
              class="history-item"
              :class="{ 'is-handled': h.handled }"
            >
              <span class="history-date">{{ h.alertDate }}</span>
              <span class="history-msg" :style="{ color: levelColor(h.level) }">{{ h.message }}</span>
              <van-button
                size="mini"
                plain
                class="undo-btn"
                :loading="alertHandlingKey === 'h' + h.id"
                @click="resolveAlert(h, !h.handled)"
              >{{ h.handled ? '取消' : '已处理' }}</van-button>
            </div>
          </div>
        </van-tab>

        <!-- 权益 -->
        <van-tab title="权益">
          <div class="equity-card">
            <template v-if="equityCurve.length >= 2">
              <svg viewBox="0 0 320 96" class="spark" preserveAspectRatio="none">
                <polyline
                  :points="equityPoints"
                  fill="none"
                  :stroke="equityUp ? '#ee0a24' : '#07c160'"
                  stroke-width="1.6"
                />
              </svg>
              <div class="equity-meta">
                <span>起 {{ fmtMoney(equityCurve[0].equity) }}</span>
                <span>今 {{ fmtMoney(equityCurve[equityCurve.length - 1].equity) }}</span>
                <span>峰 {{ fmtMoney(summary.peakEquity) }}</span>
              </div>
              <div class="equity-meta">
                <span>{{ equityCurve.length }} 个交易日</span>
                <span :class="chgClass(summary.totalPnlPct)">
                  累计 {{ fmtPct(summary.totalPnlPct) }}
                </span>
              </div>
            </template>
            <van-empty v-else image-size="72" description="权益快照每天记录一次，明天开始就能看到曲线" />
          </div>
        </van-tab>
      </van-tabs>
    </template>

    <!-- ═══ 底部主操作 ═══ -->
    <div v-if="state" class="bottom-bar">
      <van-button block type="info" icon="edit" @click="openTrade()">手动交易</van-button>
    </div>

    <!-- ═══ 账户操作面板 ═══ -->
    <van-action-sheet
      v-model="accountSheet"
      :actions="accountActions"
      cancel-text="取消"
      close-on-click-action
      @select="onAccountAction"
    />

    <!-- ═══ 手动交易 ═══ -->
    <van-popup
      v-model="tradeVisible"
      position="bottom"
      round
      class="sim-popup"
      :style="{ maxHeight: '92%' }"
    >
      <div class="pop-head">
        <span class="pop-title">手动交易</span>
        <van-icon name="cross" class="pop-close" @click="tradeVisible = false" />
      </div>
      <div class="pop-body">
        <div class="pop-hint">
          可用现金 <b>{{ fmtMoney(summary.cash) }}</b> · 组合权益 <b>{{ fmtMoney(summary.equity) }}</b>
          <div class="muted">模拟成交全天候受理，不校验开市时段；买入须 100 股整数倍</div>
        </div>

        <van-field
          v-model="tradeForm.code"
          label="股票代码"
          placeholder="6 位代码"
          type="number"
          maxlength="6"
          @blur="fetchQuote"
        >
          <template #button>
            <van-button size="mini" type="info" :loading="quoteLoading" @click="fetchQuote">查价</van-button>
          </template>
        </van-field>

        <van-field
          v-if="quote"
          label="最新行情"
          readonly
          :value="`${quote.name || '—'} · ${fmtPrice(quote.price)}`"
        >
          <template #button>
            <span class="quote-src">{{ quote.source === 'realtime' ? '实时' : `收盘 ${quote.updatedAt || ''}` }}</span>
          </template>
        </van-field>
        <div v-else-if="quoteError" class="pop-error">{{ quoteError }}</div>

        <van-field name="side" label="方向">
          <template #input>
            <van-radio-group v-model="tradeForm.side" direction="horizontal" @change="onSideChange">
              <van-radio name="buy">买入</van-radio>
              <van-radio name="sell">卖出</van-radio>
            </van-radio-group>
          </template>
        </van-field>

        <van-field
          v-model="tradeForm.price"
          label="成交价"
          type="digit"
          placeholder="查价自动带入，可改"
        />

        <van-field
          v-model="tradeForm.shares"
          label="股数"
          type="number"
          placeholder="100 的整数倍"
        />

        <div class="quick-row">
          <van-button
            v-for="q in shareQuick"
            :key="q"
            size="mini"
            plain
            @click="fillShares(q)"
          >{{ q }}</van-button>
          <van-button
            v-if="tradeForm.side === 'sell' && holding"
            size="mini"
            plain
            type="info"
            @click="fillShares(holding.shares)"
          >全部卖出</van-button>
          <van-button
            v-else-if="tradeForm.side === 'buy'"
            size="mini"
            plain
            type="info"
            @click="fillShares(maxBuyShares)"
          >可买 {{ maxBuyShares }}</van-button>
        </div>

        <div class="pop-summary">
          <div class="sum-line">
            <span>成交金额</span>
            <b>{{ fmtMoney(tradeAmount) }}</b>
          </div>
          <div class="sum-line">
            <span>预估费用（佣金+印花税+过户费）</span>
            <b>{{ fmtMoney(tradeFee) }}</b>
          </div>
          <div v-if="tradeForm.side === 'buy'" class="sum-line">
            <span>成交后单只占比</span>
            <b :class="overCap ? 'warn-text' : ''">
              {{ tradeWeightAfter == null ? '—' : tradeWeightAfter.toFixed(2) + '%' }}
              <em v-if="overCap">超上限 {{ thresholds.maxPosition }}%</em>
            </b>
          </div>
          <div v-else-if="holding" class="sum-line">
            <span>当前持有 {{ holding.shares.toLocaleString('zh-CN') }} 股 · 成本 {{ fmtPrice(holding.costPrice) }}</span>
            <b :class="chgClass(holding.pnlPct)">{{ fmtPct(holding.pnlPct) }}</b>
          </div>
          <div v-else-if="tradeForm.side === 'sell'" class="sum-line warn-text">
            <span>该账户没有这只股票的持仓</span>
          </div>
        </div>

        <van-field
          v-model="tradeForm.reason"
          label="备注"
          maxlength="60"
          placeholder="如：手动加仓 / 触发止损离场"
        />

        <van-checkbox
          v-if="tradeForm.side === 'buy'"
          v-model="tradeForm.force"
          icon-size="16"
          shape="square"
          class="force-check"
        >忽略单支持仓上限（{{ thresholds.maxPosition }}%）</van-checkbox>
      </div>
      <div class="pop-foot">
        <van-button block type="info" :loading="tradeSaving" :disabled="!canTrade" @click="submitTrade">
          确认{{ SIDE_CN[tradeForm.side] }}
        </van-button>
      </div>
    </van-popup>

    <!-- ═══ 新建 / 编辑账户 ═══ -->
    <van-popup
      v-model="dialogVisible"
      position="bottom"
      round
      class="sim-popup"
      :style="{ maxHeight: '92%' }"
    >
      <div class="pop-head">
        <span class="pop-title">{{ editing ? '编辑账户' : '新建模拟账户' }}</span>
        <van-icon name="cross" class="pop-close" @click="dialogVisible = false" />
      </div>
      <div class="pop-body">
        <van-field v-model="form.name" label="账户名称" maxlength="30" placeholder="如：主策略验证盘 / 我的手动盘" />

        <van-field name="manual" label="账户类型">
          <template #input>
            <van-radio-group v-model="form.manual" direction="horizontal">
              <van-radio :name="false">策略盘</van-radio>
              <van-radio :name="true">手动盘</van-radio>
            </van-radio-group>
          </template>
        </van-field>
        <div class="pop-hint muted">
          {{ form.manual
            ? '手动盘不跟踪策略：不产生调仓建议，买卖全部自己在「手动交易」里下；风控阈值照常生效'
            : '策略盘跟踪一个滚动策略，页面按当期信号给出「调出 / 到期 / 权重偏离 / 新进」四类建议' }}
        </div>

        <van-cell
          v-if="!form.manual"
          title="关联策略"
          is-link
          :value="form.strategyTag || '选择策略'"
          @click="openStrategyPicker"
        />
        <div v-if="!form.manual" class="pop-hint muted">只列出有当期信号的策略；无信号的策略无法生成建议</div>

        <van-field
          v-if="!editing"
          v-model="form.initialCash"
          label="初始资金"
          type="number"
          placeholder="1000000"
        />

        <div class="pop-section">风控阈值（按本账户生效）</div>
        <van-field v-model="form.stopLossPct" label="止损线" type="digit" placeholder="8">
          <template #button><span class="suffix">% 浮亏触及即报止损</span></template>
        </van-field>
        <van-field v-model="form.takeProfitPct" label="止盈线" type="digit" placeholder="20">
          <template #button><span class="suffix">% 浮盈触及即报止盈</span></template>
        </van-field>
        <van-field v-model="form.maxPositionPct" label="单只上限" type="digit" placeholder="20">
          <template #button><span class="suffix">% 越限买入被拒</span></template>
        </van-field>
        <van-field v-model="form.maxDrawdownPct" label="组合回撤" type="digit" placeholder="5">
          <template #button><span class="suffix">% 超限报预警</span></template>
        </van-field>

        <van-field v-model="form.note" label="备注" maxlength="60" placeholder="选填" />
      </div>
      <div class="pop-foot">
        <van-button block type="info" :loading="saving" @click="saveAccount">保存</van-button>
      </div>
    </van-popup>

    <!-- 策略选择面板 -->
    <van-popup v-model="strategyPicker" position="bottom" round :style="{ maxHeight: '70%' }">
      <div class="pop-head">
        <span class="pop-title">选择关联策略</span>
        <van-icon name="cross" class="pop-close" @click="strategyPicker = false" />
      </div>
      <div class="pop-body">
        <van-empty v-if="!validStrategies.length" image-size="72" description="当前没有带信号的策略" />
        <van-cell
          v-for="s in validStrategies"
          :key="s.tag"
          :title="s.tag"
          :label="`信号 ${s.signalDate} · ${s.pickCount} 只`"
          is-link
          @click="pickStrategy(s.tag)"
        />
      </div>
    </van-popup>

    <!-- ═══ 改成本 ═══ -->
    <van-popup
      v-model="costVisible"
      position="bottom"
      round
      class="sim-popup"
      :style="{ maxHeight: '70%' }"
    >
      <div class="pop-head">
        <span class="pop-title">修改成本价</span>
        <van-icon name="cross" class="pop-close" @click="costVisible = false" />
      </div>
      <div class="pop-body">
        <van-cell title="股票" :value="`${costForm.code} ${costForm.name}`" />
        <van-cell title="持有" :value="`${(costForm.shares || 0).toLocaleString('zh-CN')} 股`" />
        <van-cell title="原成本" :value="costForm.prevCost == null ? '—' : fmtPrice(costForm.prevCost)" />
        <van-field
          v-model="costForm.costPrice"
          label="新成本"
          type="digit"
          placeholder="填实盘买入成本（可含费）"
        />
        <div class="pop-hint muted">只改成本价与浮动盈亏口径，不变动现金、不产生成交流水</div>
      </div>
      <div class="pop-foot">
        <van-button
          block
          type="info"
          :loading="costSaving"
          :disabled="!(Number(costForm.costPrice) > 0)"
          @click="submitCostEdit"
        >保存</van-button>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { request } from '../utils/http.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'

const SIDE_CN = { buy: '买入', sell: '卖出' }
// 与服务层 DEFAULT_*_PCT 对齐；仅作网络未回时的建盘占位，保存时由服务端兜底
const RISK_FALLBACK = { stopLossPct: 8, takeProfitPct: 20, maxPositionPct: 20, maxDrawdownPct: 5 }

export default {
  name: 'MobileSimTrading',
  props: {
    visible: { type: Boolean, default: true },
  },
  data() {
    return {
      SIDE_CN,
      accounts: [],
      strategies: [],
      accountId: null,
      state: null,
      loading: false,
      error: '',
      activeTab: 0,
      onlyAbnormal: false,
      // 忽略状态一律以服务端 sim_ignore 为准：这里**不再**维护本地 ignored 数组
      // （原来的 `ignored: []` 就是「点了忽略、刷新又回来」的根因）
      ignoringKey: '',
      /** 「已处理」/「取消处理」的 loading —— 活动预警用 `a<type><code>`，历史行用 `h<id>` */
      alertHandlingKey: '',
      /** 「全部已处理」的 loading */
      alertBulkRunning: false,
      tradingKey: '',
      bulkRunning: false,
      accountSheet: false,
      // 建盘
      dialogVisible: false,
      editing: false,
      saving: false,
      strategyPicker: false,
      form: {
        name: '',
        strategyTag: '',
        manual: false,
        initialCash: 1000000,
        note: '',
        ...RISK_FALLBACK,
      },
      riskDefaults: { ...RISK_FALLBACK },
      // 手动交易
      tradeVisible: false,
      tradeSaving: false,
      quoteLoading: false,
      quote: null,
      quoteError: '',
      tradeForm: { code: '', side: 'buy', price: '', shares: '', reason: '', force: false },
      shareQuick: [100, 500, 1000, 2000],
      // 改成本
      costVisible: false,
      costSaving: false,
      costForm: { code: '', name: '', shares: 0, prevCost: null, costPrice: '' },
      refreshTimer: null,
    }
  },
  computed: {
    account() {
      return this.state ? this.state.account : null
    },
    summary() {
      return (this.state && this.state.summary) || {}
    },
    thresholds() {
      return (this.state && this.state.thresholds) || { stopLoss: -8, takeProfit: 20, maxPosition: 20, drawdown: 5 }
    },
    isDrawdownWarn() {
      return Number(this.summary.drawdown || 0) <= -Math.abs(Number(this.thresholds.drawdown || 5))
    },
    tradeWindow() {
      return (
        (this.state && this.state.tradeWindow) || {
          open: true,
          marketOpen: false,
          reason: '',
          sessionLabel: '',
        }
      )
    },
    sessionText() {
      const w = this.tradeWindow
      if (w.marketOpen) {
        return `行情开市${w.sessionLabel ? ` · ${w.sessionLabel}` : ''} · 模拟成交全天候受理`
      }
      return `行情休市${w.reason ? `（${w.reason}）` : ''} · 模拟成交仍可下单，取价用最近收盘`
    },
    signal() {
      return this.state ? this.state.signal : null
    },
    positions() {
      return (this.state && this.state.positions) || []
    },
    visiblePositions() {
      if (!this.onlyAbnormal) return this.positions
      return this.positions.filter((p) => this.statusText(p) !== '持有')
    },
    alerts() {
      return (this.state && this.state.alerts) || []
    },
    historyAlerts() {
      return ((this.state && this.state.alertHistory) || []).slice(0, 12)
    },
    /** 当日条件成立、但已被你标记「已处理」挡掉的条数 */
    alertHandledCount() {
      return (this.state && this.state.alertHandledCount) || 0
    },
    /** 台账口径：该账户全部未处理预警数（含条件已不成立的旧账）—— 首页待办算的就是它 */
    alertPendingCount() {
      return (this.state && this.state.alertPendingCount) || 0
    },
    trades() {
      return (this.state && this.state.trades) || []
    },
    equityCurve() {
      return (this.state && this.state.equityCurve) || []
    },
    equityUp() {
      const c = this.equityCurve
      return c.length < 2 ? true : c[c.length - 1].equity >= c[0].equity
    },
    equityPoints() {
      const c = this.equityCurve
      if (c.length < 2) return ''
      const vs = c.map((x) => x.equity)
      const min = Math.min(...vs)
      const max = Math.max(...vs)
      const span = max - min || 1
      return c
        .map((p, i) => `${((i / (c.length - 1)) * 320).toFixed(1)},${(92 - ((p.equity - min) / span) * 88).toFixed(1)}`)
        .join(' ')
    },
    positionPct() {
      const eq = this.summary.equity
      if (!eq) return '0.00'
      return ((this.summary.marketValue / eq) * 100).toFixed(2)
    },
    // 待确认建议：服务端已按 sim_ignore 过滤（本期信号窗口内被忽略的不再下发）
    // ⚠️ 这里**不要**再叠一层前端 ignored 过滤 —— 前端临时态一刷新就没了
    //    （PC 端 2026-10-01 就是这么坏的：「点了忽略，刷新又回来」）
    pendingSuggestions() {
      return (this.state && this.state.suggestions) || []
    },
    /** 被忽略掉的条数（服务端算的总数 − 实际下发数） */
    ignoredCount() {
      return (this.state && this.state.ignoredCount) || 0
    },
    /** 忽略记录里「本期仍生效」的（`active` 由服务端按当前信号窗口判定） */
    activeIgnores() {
      return ((this.state && this.state.ignores) || []).filter((x) => x.active)
    },
    quoteFreshness() {
      const srcs = this.positions.map((p) => p.priceSource)
      if (!srcs.length) return '—'
      if (srcs.every((s) => s === 'realtime')) return '实时'
      if (srcs.every((s) => s === 'daily')) return '最近收盘'
      return '部分实时'
    },
    validStrategies() {
      return this.strategies.filter((s) => s.hasSignal)
    },
    accountOptions() {
      return this.accounts.map((a) => ({
        text: `${a.name}${a.status === 'closed' ? '（已关闭）' : ''}`,
        value: a.id,
      }))
    },
    accountActions() {
      if (!this.account) return [{ name: '新建账户' }]
      const isActive = this.account.status === 'active'
      return [
        { name: '手动交易', subname: '按最新价买入或卖出任意股票' },
        { name: '新建账户' },
        { name: '编辑账户', subname: '名称 / 类型 / 关联策略 / 风控阈值' },
        {
          name: isActive ? '关闭账户' : '启用账户',
          subname: isActive ? '关闭后不再更新建议与预警' : '重新纳入建议与预警',
        },
        { name: '删除账户', color: '#ee0a24', subname: '持仓、流水、预警一并清除' },
      ]
    },
    /* ----- 手动交易辅助 ----- */
    holding() {
      const code = String(this.tradeForm.code || '').replace(/\D/g, '')
      if (code.length !== 6) return null
      return this.positions.find((p) => p.code === code) || null
    },
    tradeAmount() {
      const p = Number(this.tradeForm.price)
      const s = Number(this.tradeForm.shares)
      if (!Number.isFinite(p) || !Number.isFinite(s) || p <= 0 || s <= 0) return 0
      return Math.round(p * s * 100) / 100
    },
    // 与服务端 calcFee 同口径：佣金（双边有最低）+ 印花税（仅卖出）+ 过户费
    tradeFee() {
      const a = this.account
      const amount = this.tradeAmount
      if (!a || !amount) return 0
      const commission = Math.max(amount * a.feeRate, a.minFee)
      const stamp = this.tradeForm.side === 'sell' ? amount * a.stampRate : 0
      const transfer = amount * a.transferRate
      return Math.round((commission + stamp + transfer) * 100) / 100
    },
    tradeWeightAfter() {
      if (this.tradeForm.side !== 'buy') return null
      const equity = Number(this.summary.equity) || 0
      if (!equity || !this.tradeAmount) return null
      const current = this.holding ? this.holding.marketValue : 0
      return Math.round(((current + this.tradeAmount) / equity) * 10000) / 100
    },
    overCap() {
      return this.tradeWeightAfter != null && this.tradeWeightAfter > Number(this.thresholds.maxPosition) + 0.005
    },
    maxBuyShares() {
      const price = Number(this.tradeForm.price)
      const cash = Number(this.summary.cash) || 0
      if (!(price > 0) || !cash) return 0
      // 留 0.5% 余量覆盖佣金/过户费，避免提交时因费用超现而被拒
      const lots = Math.floor((cash * 0.995) / price / 100)
      return Math.max(lots, 0) * 100
    },
    canTrade() {
      if (!this.account) return false
      const code = String(this.tradeForm.code || '').replace(/\D/g, '')
      const s = Number(this.tradeForm.shares)
      if (code.length !== 6 || !(Number(this.tradeForm.price) > 0)) return false
      if (!(s > 0) || s % 100 !== 0) return false
      if (this.tradeForm.side === 'sell') {
        if (!this.holding) return false
        if (s > this.holding.shares) return false
      } else if (this.tradeAmount + this.tradeFee > Number(this.summary.cash || 0) + 0.005) {
        return false
      }
      return true
    },
  },
  watch: {
    visible(val) {
      if (val && !this.state) this.load()
    },
  },
  created() {
    this.load()
  },
  mounted() {
    // 页面常驻（v-show）时跨过开/收盘要自己更新时段文案
    this.refreshTimer = setInterval(() => {
      if (!this.visible || !this.state || this.loading) return
      if (this.tradeVisible || this.dialogVisible || this.costVisible) return
      if (typeof document !== 'undefined' && document.hidden) return
      this.loadDashboard()
    }, 60 * 1000)
  },
  beforeDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
  },
  methods: {
    eastmoneyCodeLink,
    stockstarCodeLink,
    async load() {
      await this.loadAccounts()
      await this.loadDashboard()
    },
    async loadAccounts() {
      try {
        const res = await request('/api/sim/accounts')
        this.accounts = res.list || []
        if (!this.accountId && this.accounts.length) this.accountId = this.accounts[0].id
      } catch (e) {
        this.error = e.message
      }
    },
    async loadStrategies() {
      if (this.strategies.length) return
      try {
        const res = await request('/api/sim/strategies')
        this.strategies = res.list || []
        if (res.defaults) this.riskDefaults = { ...RISK_FALLBACK, ...res.defaults }
      } catch (e) {
        /* 建盘时再提示 */
      }
    },
    async loadDashboard() {
      if (!this.accountId) {
        this.state = null
        return
      }
      this.loading = true
      this.error = ''
      try {
        this.state = await request(`/api/sim/dashboard?accountId=${this.accountId}`)
      } catch (e) {
        this.state = null
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    onAccountChange() {
      this.loadDashboard()
    },
    /** 忽略一条建议 —— 落库（sim_ignore），接口回传过滤好的新 state */
    async ignore(s) {
      this.ignoringKey = s.side + s.code
      try {
        this.state = await request('/api/sim/suggestion/ignore', {
          method: 'POST',
          body: JSON.stringify({
            accountId: this.accountId,
            code: s.code,
            side: s.side,
            name: s.name,
            reason: s.reason,
          }),
        })
        this.$toast.success(`已忽略 ${s.code}，本期信号不再提示`)
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.ignoringKey = ''
      }
    },
    /** 撤销一条忽略（记录在 PC 端「预警 → 已忽略的建议」里；手机端也可从这里恢复） */
    async unignore(row) {
      try {
        this.state = await request('/api/sim/suggestion/unignore', {
          method: 'POST',
          body: JSON.stringify({ accountId: this.accountId, id: row.id }),
        })
        this.$toast.success(`已恢复 ${row.code} 的建议`)
      } catch (e) {
        this.$toast.fail(e.message)
      }
    },
    async tradeOne(s) {
      if (!s.price || !s.shares) return
      this.tradingKey = s.side + s.code
      try {
        this.state = await request('/api/sim/trade', {
          method: 'POST',
          body: JSON.stringify({
            accountId: this.accountId,
            code: s.code,
            name: s.name,
            side: s.side,
            price: s.price,
            shares: s.shares,
            reason: s.reason,
            signalDate: this.signal ? this.signal.signalDate : '',
          }),
        })
        this.$toast.success(`${SIDE_CN[s.side]} ${s.code} 已按建议成交`)
        await this.loadAccounts()
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.tradingKey = ''
      }
    },
    // 逐条串行：服务端每次都会重算现金与持仓，并行会互相覆盖
    async tradeAll() {
      this.bulkRunning = true
      const list = [...this.pendingSuggestions]
      let ok = 0
      let fail = 0
      for (const s of list) {
        if (!s.price || !s.shares || s.affordable === false) {
          fail += 1
          continue
        }
        try {
          this.state = await request('/api/sim/trade', {
            method: 'POST',
            body: JSON.stringify({
              accountId: this.accountId,
              code: s.code,
              name: s.name,
              side: s.side,
              price: s.price,
              shares: s.shares,
              reason: s.reason,
              signalDate: this.signal ? this.signal.signalDate : '',
            }),
          })
          ok += 1
        } catch (e) {
          fail += 1
        }
      }
      this.bulkRunning = false
      await this.loadAccounts()
      const text = `成交 ${ok} 笔${fail ? `，跳过/失败 ${fail} 笔` : ''}`
      if (fail) this.$toast(text)
      else this.$toast.success(text)
    },
    async resolveAlert(a, handled = true) {
      // ⚠️ 优先用服务端在活动预警上带回来的台账 id。
      //    老写法是拿 (type, code) 去 historyAlerts 里反查，而那个列表被 slice(0, 12) 截断过
      //    ⇒ 匹配行不在前 12 条时静默 return，按钮点了没反应。
      const id = a.id != null
        ? a.id
        : (this.historyAlerts.find((h) => h.type === a.type && h.code === a.code && h.handled === !handled) || {}).id
      if (!id) {
        this.$toast('找不到这条预警的台账记录，请下拉刷新后重试')
        return
      }
      this.alertHandlingKey = a.id != null ? this.alertKey(a) : 'h' + id
      try {
        this.state = await request('/api/sim/alert/handle', {
          method: 'POST',
          body: JSON.stringify({ accountId: this.accountId, alertId: id, handled }),
        })
        this.$toast.success(handled ? '已标记处理' : '已撤销处理')
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.alertHandlingKey = ''
      }
    },
    /** 活动预警的 loading key（列表项没有唯一 key，用 type+code 足够区分） */
    alertKey(a) {
      return `a${a.type}${a.code}`
    },
    /**
     * 一次性确认该账户全部未处理预警。
     * 用途：条件已不成立的旧预警不在活动列表里、历史列表又只渲染 12 条 ⇒ 逐条点根本点不到。
     */
    async resolveAllAlerts() {
      this.alertBulkRunning = true
      try {
        const before = this.alertPendingCount
        this.state = await request('/api/sim/alert/handle-all', {
          method: 'POST',
          body: JSON.stringify({ accountId: this.accountId }),
        })
        this.$toast.success(`已确认全部 ${before} 条未处理预警`)
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.alertBulkRunning = false
      }
    },
    /* ----- 账户操作 ----- */
    openAccountActions() {
      this.accountSheet = true
    },
    async onAccountAction(action) {
      const name = action && action.name
      if (name === '手动交易') return this.openTrade()
      if (name === '新建账户') return this.openCreate()
      if (name === '编辑账户') return this.openEdit()
      if (name === '关闭账户' || name === '启用账户') {
        try {
          await request(`/api/sim/accounts/${this.accountId}`, {
            method: 'PUT',
            body: JSON.stringify({ status: this.account.status === 'active' ? 'closed' : 'active' }),
          })
          await this.loadAccounts()
          await this.loadDashboard()
          this.$toast.success(name === '关闭账户' ? '账户已关闭' : '账户已启用')
        } catch (e) {
          this.$toast.fail(e.message)
        }
        return
      }
      if (name === '删除账户') {
        try {
          await this.$dialog.confirm({
            title: '确认删除',
            message: `删除账户「${this.account.name}」？持仓、流水、预警将一并清除。`,
          })
        } catch {
          return
        }
        try {
          await request(`/api/sim/accounts/${this.accountId}`, { method: 'DELETE' })
          this.accountId = null
          this.state = null
          await this.loadAccounts()
          await this.loadDashboard()
          this.$toast.success('已删除')
        } catch (e) {
          this.$toast.fail(e.message)
        }
      }
    },
    openCreate() {
      this.editing = false
      this.form = {
        name: '',
        strategyTag: '',
        manual: false,
        initialCash: 1000000,
        note: '',
        ...this.riskDefaults,
      }
      this.loadStrategies()
      this.dialogVisible = true
    },
    openEdit() {
      if (!this.account) return
      this.editing = true
      this.form = {
        name: this.account.name,
        strategyTag: this.account.strategyTag,
        manual: !!this.account.isManual,
        initialCash: this.account.initialCash,
        note: this.account.note || '',
        stopLossPct: this.account.stopLossPct,
        takeProfitPct: this.account.takeProfitPct,
        maxPositionPct: this.account.maxPositionPct,
        maxDrawdownPct: this.account.maxDrawdownPct,
      }
      this.loadStrategies()
      this.dialogVisible = true
    },
    async openStrategyPicker() {
      await this.loadStrategies()
      this.strategyPicker = true
    },
    pickStrategy(tag) {
      this.form.strategyTag = tag
      this.strategyPicker = false
    },
    async saveAccount() {
      if (this.saving) return
      if (!String(this.form.name || '').trim()) return this.$toast('请填写账户名称')
      if (!this.form.manual && !this.form.strategyTag) return this.$toast('请选择关联策略')
      this.saving = true
      const risk = {
        stopLossPct: Number(this.form.stopLossPct),
        takeProfitPct: Number(this.form.takeProfitPct),
        maxPositionPct: Number(this.form.maxPositionPct),
        maxDrawdownPct: Number(this.form.maxDrawdownPct),
      }
      try {
        if (this.editing) {
          await request(`/api/sim/accounts/${this.accountId}`, {
            method: 'PUT',
            body: JSON.stringify({
              name: this.form.name,
              note: this.form.note,
              manual: this.form.manual,
              strategyTag: this.form.manual ? '' : this.form.strategyTag,
              ...risk,
            }),
          })
        } else {
          const res = await request('/api/sim/accounts', {
            method: 'POST',
            body: JSON.stringify({
              ...this.form,
              initialCash: Number(this.form.initialCash) || 1000000,
              ...risk,
            }),
          })
          this.accountId = res.id
        }
        this.dialogVisible = false
        await this.loadAccounts()
        await this.loadDashboard()
        this.$toast.success('已保存')
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.saving = false
      }
    },
    /* ----- 手动交易 ----- */
    openTrade(row) {
      if (!this.account) return this.$toast('请先新建模拟账户')
      // ⚠️ @click="openTrade" 会把 MouseEvent 当第一个参数传进来，
      // 不能只判 truthy，必须按「有 code 字段」识别是不是真的持仓行
      const r = row && typeof row === 'object' && row.code ? row : null
      this.quote = null
      this.quoteError = ''
      this.tradeForm = {
        code: r ? r.code : '',
        side: r ? 'sell' : 'buy',
        price: r ? String(r.price) : '',
        shares: '',
        reason: r ? '手动卖出' : '手动买入',
        force: false,
      }
      this.tradeVisible = true
      if (r) this.fetchQuote()
    },
    fillShares(n) {
      const v = Number(n)
      if (!(v > 0)) return
      this.tradeForm.shares = String(Math.floor(v / 100) * 100)
    },
    onSideChange() {
      this.tradeForm.shares = ''
      this.tradeForm.force = false
      if (this.tradeForm.side === 'sell' && this.holding && !Number(this.tradeForm.price)) {
        this.tradeForm.price = String(this.holding.price)
      }
    },
    async fetchQuote() {
      const code = String(this.tradeForm.code || '').replace(/\D/g, '')
      if (code.length !== 6) {
        this.quote = null
        this.quoteError = code.length ? '请输入 6 位股票代码' : ''
        return
      }
      this.quoteLoading = true
      this.quoteError = ''
      try {
        const res = await request(`/api/sim/quote?code=${code}`)
        this.quote = res.quote || null
        if (this.quote) {
          this.tradeForm.price = String(this.quote.price)
        } else {
          this.quoteError = `未找到 ${code} 的行情（无实时快照也无日线）`
        }
      } catch (e) {
        this.quote = null
        this.quoteError = e.message
      } finally {
        this.quoteLoading = false
      }
    },
    async submitTrade() {
      // busy 必须在 await 之前同步置位：按钮 loading 要等重渲染，连点两下会重复下单
      if (!this.canTrade || this.tradeSaving) return
      this.tradeSaving = true
      try {
        this.state = await request('/api/sim/trade', {
          method: 'POST',
          body: JSON.stringify({
            accountId: this.accountId,
            code: this.tradeForm.code,
            name: this.quote ? this.quote.name : '',
            side: this.tradeForm.side,
            price: Number(this.tradeForm.price),
            shares: Number(this.tradeForm.shares),
            reason: this.tradeForm.reason,
            force: this.tradeForm.force === true,
          }),
        })
        this.$toast.success(
          `${SIDE_CN[this.tradeForm.side]} ${this.tradeForm.code} ${Number(this.tradeForm.shares).toLocaleString('zh-CN')} 股已成交`,
        )
        this.tradeVisible = false
        await this.loadAccounts()
      } catch (e) {
        this.$toast.fail(e.message)
        // 被上限拦下时保留弹窗，让用户改股数或勾选忽略
        if (/单支持仓/.test(e.message)) this.tradeForm.force = false
      } finally {
        this.tradeSaving = false
      }
    },
    /* ----- 改成本 ----- */
    openCostEdit(row) {
      if (!row || !row.code) return
      this.costForm = {
        code: row.code,
        name: row.name || '',
        shares: row.shares,
        prevCost: row.costPrice,
        costPrice: String(row.costPrice),
      }
      this.costVisible = true
    },
    async submitCostEdit() {
      if (this.costSaving) return
      const cost = Number(this.costForm.costPrice)
      if (!(cost > 0)) return this.$toast('请输入有效成本价')
      this.costSaving = true
      try {
        this.state = await request('/api/sim/position/cost', {
          method: 'PUT',
          body: JSON.stringify({
            accountId: this.accountId,
            code: this.costForm.code,
            costPrice: cost,
          }),
        })
        this.costVisible = false
        this.$toast.success(`${this.costForm.code} 成本已改为 ${cost.toFixed(3)}`)
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.costSaving = false
      }
    },
    /* ----- 展示辅助（A 股口径：涨红跌绿） ----- */
    chgClass(v) {
      const n = Number(v)
      if (!Number.isFinite(n) || n === 0) return ''
      return n > 0 ? 'up' : 'down'
    },
    fmtMoney(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      return `¥${n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    fmtSigned(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      return `${n > 0 ? '+' : n < 0 ? '−' : ''}¥${Math.abs(n).toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    },
    fmtPct(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      return `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(2)}%`
    },
    fmtPrice(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : '—'
    },
    levelColor(level) {
      return { danger: '#A32D2D', warn: '#BA7517', info: '#185FA5' }[level] || '#5F5E5A'
    },
    statusText(row) {
      const th = this.thresholds
      if (row.pnlPct <= Number(th.stopLoss != null ? th.stopLoss : -8)) return '止损'
      if (row.weight > Number(th.maxPosition || 20) + 0.005) return '超配'
      if (row.exitDue) return '到期'
      const s = this.pendingSuggestions.find((x) => x.code === row.code && x.side === 'sell')
      if (s) return '待调出'
      if (row.pctChg != null && Math.abs(row.pctChg) >= Number(th.surge || 9.5)) return '异动'
      return '持有'
    },
    statusTagType(row) {
      const t = this.statusText(row)
      if (t === '持有') return 'info'
      if (t === '异动' || t === '超配') return 'warning'
      return 'danger'
    },
  },
}
</script>

<style scoped>
.m-sim {
  min-height: 60vh;
  background: #f7f8fa;
}

/* 底部主操作占位，避免内容被固定栏遮住 */
.m-sim.has-bottom {
  padding-bottom: 62px;
}

/* ═══ 账户栏 ═══ */
.sim-top {
  position: sticky;
  top: calc(46px + env(safe-area-inset-top, 0px));
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 4px;
  padding-right: 12px;
  background: #fff;
  border-bottom: 1px solid #ebedf0;
}

.acct-menu {
  flex: 1;
  min-width: 0;
}

.acct-menu >>> .van-dropdown-menu__bar {
  box-shadow: none;
  height: 44px;
}

.acct-menu >>> .van-dropdown-menu__title {
  max-width: 100%;
  font-size: 14px;
}

.acct-none {
  flex: 1;
  padding: 0 16px;
  font-size: 13px;
  color: #969799;
  line-height: 44px;
}

.top-icon {
  font-size: 19px;
  color: #646566;
  padding: 4px 6px;
}

.spacer {
  flex: 1 1 auto;
}

.page-loading {
  padding: 64px 0;
}

/* ═══ 资产概览 ═══ */
.asset-card {
  margin: 10px 12px 0;
  padding: 14px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.asset-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.asset-label {
  font-size: 12px;
  color: #969799;
}

.asset-value {
  margin-top: 4px;
  font-size: 24px;
  font-weight: 600;
  color: #323233;
  line-height: 1.2;
  word-break: break-all;
}

.asset-pnl {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
  font-size: 13px;
}

.asset-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f2f3f5;
}

.g-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.g-label {
  font-size: 11px;
  color: #969799;
}

.g-cell b {
  font-size: 13px;
  font-weight: 500;
  color: #323233;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.asset-foot,
.asset-signal {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin-top: 10px;
  font-size: 11px;
  color: #969799;
}

.asset-signal {
  flex-direction: column;
  gap: 2px;
  padding-top: 8px;
  border-top: 1px solid #f2f3f5;
}

/* ═══ 待确认操作 ═══ */
.sug-card {
  margin: 10px 12px 0;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.sug-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #fdf6ec;
  font-size: 13px;
  color: #b88230;
}

.sug-card.is-empty .sug-head {
  background: #f4f4f5;
  color: #969799;
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e6a23c;
  flex: 0 0 auto;
}

.sug-card.is-empty .dot {
  background: #c0c4cc;
}

.sug-title {
  font-weight: 500;
}

/* 待确认操作条上的「已忽略 N 条」—— 说明建议为什么变少，不能只靠 toast 一闪而过 */
.sug-note.is-ignored {
  flex: 0 0 auto;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 11px;
  color: #e6a23c;
  background: #fdf6ec;
  border-radius: 3px;
}

.sug-body {
  padding: 4px 0;
}

.sug-row {
  padding: 10px 12px;
  border-bottom: 1px solid #f5f6f7;
}

.sug-row:last-child {
  border-bottom: none;
}

.sug-l1 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.sug-stock {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #323233;
}

.sug-stock a {
  color: inherit;
  text-decoration: none;
  margin-right: 4px;
}

.sug-stock a:first-child {
  color: #1989fa;
}

.sug-amount {
  font-size: 13px;
  color: #323233;
}

.sug-l2 {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 6px;
  font-size: 12px;
  color: #646566;
}

.sug-reason {
  flex: 1 1 100%;
  color: #969799;
  font-size: 11px;
}

.sug-l3 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.sug-empty {
  padding: 18px 12px;
  text-align: center;
  font-size: 12px;
  color: #969799;
}

/* ═══ Tabs ═══ */
.detail-tabs {
  margin-top: 10px;
}

.detail-tabs >>> .van-tabs__wrap {
  border-bottom: 1px solid #ebedf0;
}

.detail-tabs >>> .van-tab {
  font-size: 13px;
}

.tab-bar {
  display: flex;
  align-items: center;
  padding: 10px 12px 4px;
  font-size: 12px;
}

/* ═══ 持仓卡片 ═══ */
.pos-card {
  margin: 8px 12px;
  padding: 12px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.pos-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.code {
  font-size: 13px;
  color: #1989fa;
  text-decoration: none;
}

.pname {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 15px;
  font-weight: 600;
  color: #323233;
  text-decoration: none;
}

.pos-line {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 6px;
  font-size: 12px;
  color: #969799;
}

.pos-line b {
  font-weight: 500;
  color: #323233;
}

.stale {
  font-style: normal;
  margin-left: 3px;
  font-size: 10px;
  color: #ed6a0c;
}

.pos-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #f2f3f5;
}

/* ═══ 流水 ═══ */
.flow-card {
  margin: 8px 12px;
  padding: 12px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.flow-l1 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.flow-stock {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #323233;
}

.flow-stock a {
  color: inherit;
  text-decoration: none;
  margin-right: 4px;
}

.flow-stock a:first-child {
  color: #1989fa;
}

.flow-date {
  font-size: 12px;
  color: #969799;
}

.flow-l2 {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  margin-top: 6px;
  font-size: 12px;
  color: #646566;
}

.flow-l2 b {
  font-weight: 500;
}

.flow-reason {
  margin-top: 6px;
  font-size: 11px;
  color: #969799;
}

/* ═══ 预警 ═══ */
.alert-list {
  padding: 4px 0;
}

.alert-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 8px 12px;
  padding: 12px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.lv-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  margin-top: 6px;
  flex: 0 0 auto;
}

.alert-main {
  flex: 1;
  min-width: 0;
}

.alert-msg {
  font-size: 13px;
  line-height: 1.5;
}

.alert-detail {
  margin-top: 3px;
  font-size: 11px;
  color: #969799;
  word-break: break-all;
}

.history {
  margin: 12px 12px 0;
  padding: 12px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.history-head {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #969799;
  margin-bottom: 6px;
}

.history-head-text {
  flex: 1 1 auto;
  min-width: 0;
}

/* 标题行右侧的动作（如「全部已处理」）—— 靠 margin-left:auto 顶到最右 */
.history-head .head-action {
  flex: 0 0 auto;
  margin-left: auto;
}

.history-item {
  display: flex;
  gap: 8px;
  padding: 4px 0;
  font-size: 11px;
  color: #646566;
}

.history-item.is-handled {
  opacity: 0.45;
}

.history-date {
  flex: 0 0 66px;
  color: #969799;
}

.history-msg {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-flag {
  flex: 0 0 auto;
  color: #969799;
}

/* 「已忽略的建议」区块：沿用 .history 版式，只加撤销按钮的位置与一条分隔线做区分 */
.history.ignores {
  margin-top: 8px;
  border-top: 1px solid #ebedf0;
}
.history.ignores .history-item {
  align-items: center;
}
/* 忽略条目是「决策记录」，不是风险文案 ⇒ 不要 levelColor 的红/黄，保持中性 */
.history-msg.is-plain {
  color: #646566;
}
.undo-btn {
  flex: 0 0 auto;
  height: auto;
  padding: 0 6px;
  font-size: 11px;
}

/* ═══ 权益 ═══ */
.equity-card {
  margin: 8px 12px;
  padding: 12px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.spark {
  width: 100%;
  height: 96px;
  border-bottom: 1px solid #ebedf0;
}

.equity-meta {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  font-size: 11px;
  color: #969799;
}

/* ═══ 底部主操作 ═══ */
.bottom-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: calc(50px + env(safe-area-inset-bottom, 0px));
  z-index: 99;
  padding: 8px 12px;
  background: #fff;
  border-top: 1px solid #ebedf0;
}

/* ═══ 弹出层 ═══ */
.sim-popup {
  display: flex;
  flex-direction: column;
}

.pop-head {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  position: relative;
  height: 46px;
  border-bottom: 1px solid #ebedf0;
}

.pop-title {
  font-size: 16px;
  font-weight: 600;
  color: #323233;
}

.pop-close {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 18px;
  color: #969799;
}

.pop-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 4px;
}

.pop-foot {
  flex: 0 0 auto;
  padding: 10px 14px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #ebedf0;
  background: #fff;
}

.pop-hint {
  padding: 10px 16px 4px;
  font-size: 12px;
  color: #646566;
  line-height: 1.6;
}

.pop-hint b {
  color: #323233;
}

.pop-error {
  padding: 4px 16px;
  font-size: 12px;
  color: #ee0a24;
}

.pop-section {
  margin: 12px 16px 0;
  padding-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #646566;
  border-bottom: 1px solid #ebedf0;
}

.quick-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 16px 2px;
}

.force-check {
  padding: 12px 16px 4px;
  font-size: 12px;
}

.pop-summary {
  margin: 10px 16px 4px;
  padding: 10px 12px;
  background: #f7f8fa;
  border-radius: 8px;
}

.sum-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 3px 0;
  font-size: 12px;
  color: #646566;
}

.sum-line b {
  font-weight: 500;
  color: #323233;
  text-align: right;
}

.sum-line em {
  font-style: normal;
  margin-left: 4px;
  color: #ed6a0c;
}

.quote-src {
  font-size: 12px;
  color: #969799;
}

.suffix {
  font-size: 11px;
  color: #969799;
}

/* ═══ 涨跌色（A 股口径：涨红跌绿） ═══ */
.up {
  color: #ee0a24;
}

.down {
  color: #07c160;
}

.down-strong {
  color: #07c160;
  font-weight: 600;
}

.warn-text {
  color: #ed6a0c;
}

.muted {
  color: #969799;
}
</style>
